import React, { useState, useEffect } from 'react';
import {
  Headphones, Plus, Users, Play, CheckCircle, BarChart2,
  Edit2, Trash2, Save, List, Zap, Star, FilePen, CheckCircle2
} from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import AdminTable from '../../components/admin/AdminTable';
import AdminCard from '../../components/admin/AdminCard';
import AdminModal from '../../components/admin/AdminModal';
import api from '../../services/api';
import { useProgram } from '../../contexts/ProgramContext';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import '../../styles/pages/admin/MeditationManagement.css';
import '../../styles/admin-shared.css';

const MeditationManagement = () => {
  // Re-fetch on program switch
  const { slug } = useProgram();
  const [sessions, setSessions] = useState([]);
  const [analytics, setAnalytics] = useState({
    total_sessions: 0,
    published_sessions: 0,
    total_users_active: 0,
    total_listening_sessions: 0,
    total_minutes_listened: 0,
    completed_sessions: 0,
    completion_rate: 0,
    by_category: [],
    top_sessions: []
  });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [audioFile, setAudioFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    duration_seconds: 600,
    audio_url: '',
    thumbnail_url: '',
    category: 'mindfulness',
    difficulty_level: 'EASY',
    instructor: '',
    is_emergency: false,
    is_premium: false,
    published: true,
    tags: '',
    benefits: ''
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [sessionsData, analyticsData] = await Promise.all([
        api.get('/admin/meditation/sessions').catch(() => ({ data: { data: [] } })),
        api.get('/admin/meditation/analytics').catch(() => ({ data: { data: {} } }))
      ]);

      setSessions(sessionsData.data.data || []);
      setAnalytics(analyticsData.data.data || {
        total_sessions: 0,
        published_sessions: 0,
        total_users_active: 0,
        total_listening_sessions: 0,
        total_minutes_listened: 0,
        completed_sessions: 0,
        completion_rate: 0,
        by_category: [],
        top_sessions: []
      });
    } catch (error) {
      console.error('Erro ao carregar meditações:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (session = null) => {
    if (session) {
      setEditing(session);
      setFormData({
        title: session.title || '',
        description: session.description || '',
        duration_seconds: session.duration_seconds || 600,
        audio_url: session.audio_url || '',
        thumbnail_url: session.thumbnail_url || '',
        category: session.category || 'mindfulness',
        difficulty_level: session.difficulty_level || 'EASY',
        instructor: session.instructor || '',
        is_emergency: session.is_emergency || false,
        is_premium: session.is_premium || false,
        published: session.published ?? true,
        tags: Array.isArray(session.tags) ? session.tags.join(', ') : '',
        benefits: Array.isArray(session.benefits) ? session.benefits.join(', ') : ''
      });
    } else {
      setEditing(null);
      setFormData({
        title: '',
        description: '',
        duration_seconds: 600,
        audio_url: '',
        thumbnail_url: '',
        category: 'mindfulness',
        difficulty_level: 'EASY',
        instructor: '',
        is_emergency: false,
        is_premium: false,
        published: true,
        tags: '',
        benefits: ''
      });
    }
    setAudioFile(null);
    setThumbnailFile(null);
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      
      // Append all form fields
      Object.keys(formData).forEach(key => {
        if (key !== 'audio_url' && key !== 'thumbnail_url') {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Append files if selected
      if (audioFile) {
        formDataToSend.append('audio', audioFile);
      }
      
      if (thumbnailFile) {
        formDataToSend.append('thumbnail', thumbnailFile);
      }

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };

      if (editing) {
        await api.put(`/admin/meditation/sessions/${editing.id}`, formDataToSend, config);
      } else {
        if (!audioFile) {
          alert('Arquivo de áudio é obrigatório');
          return;
        }
        await api.post('/admin/meditation/sessions', formDataToSend, config);
      }
      
      setShowModal(false);
      setEditing(null);
      setAudioFile(null);
      setThumbnailFile(null);
      fetchData();
    } catch (error) {
      console.error('Erro ao salvar sessão:', error);
      alert(error.response?.data?.message || 'Erro ao salvar sessão');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente excluir esta sessão de meditação?')) return;
    
    try {
      await api.delete(`/admin/meditation/sessions/${id}`);
      fetchData();
    } catch (error) {
      console.error('Erro ao excluir sessão:', error);
      alert(error.response?.data?.message || 'Erro ao excluir sessão');
    }
  };

  const handleTogglePublish = async (id, currentStatus) => {
    try {
      await api.put(`/admin/meditation/sessions/${id}`, {
        published: !currentStatus
      });
      fetchData();
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      alert(error.response?.data?.message || 'Erro ao alterar status');
    }
  };

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes} min`;
  };

  const getCategoryIcon = () => null;

  const getCategoryLabel = (category) => {
    const labels = {
      'emergency': 'Emergência',
      'breathing': 'Respiração',
      'body_scan': 'Body Scan',
      'mindfulness': 'Mindfulness',
      'sleep': 'Sono',
      'stress_relief': 'Alívio de Estresse',
      'urge_surfing': 'Urge Surfing',
      'gratitude': 'Gratidão',
      'visualization': 'Visualização'
    };
    return labels[category] || category;
  };

  const filteredSessions = sessions.filter(session => {
    if (activeTab === 'all') return true;
    if (activeTab === 'emergency') return session.is_emergency;
    if (activeTab === 'premium') return session.is_premium;
    if (activeTab === 'published') return session.published;
    if (activeTab === 'draft') return !session.published;
    return session.category === activeTab;
  });

  const getFileUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    // Se for placeholder local, usar um SVG data URI
    if (url.includes('/assets/') || url.includes('placeholder')) {
      return null; // Retorna null para não tentar carregar
    }
    return `http://localhost:3000${url}`;
  };

  const columns = [
    {
      key: 'title',
      label: 'Sessão',
      render: (value, row) => (
        <div className="content-cell">
          {row.thumbnail_url && getFileUrl(row.thumbnail_url) ? (
            <img src={getFileUrl(row.thumbnail_url)} alt={value} className="content-thumbnail" />
          ) : row.thumbnail_url ? (
            <div className="content-thumbnail-placeholder">
              <span className="thumbnail-icon">{getCategoryIcon(row.category)}</span>
            </div>
          ) : null}
          <div>
            <div className="content-title">{value}</div>
            <div className="content-meta">
              {getCategoryLabel(row.category)} • {formatDuration(row.duration_seconds)}
              {row.is_emergency && ' • EMERGÊNCIA'}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'instructor',
      label: 'Instrutor',
      render: (value) => value || 'Não informado'
    },
    {
      key: 'play_count',
      label: 'Reproduções',
      render: (value) => <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Play size={14} /> {value || 0}</span>
    },
    {
      key: 'published',
      label: 'Status',
      render: (value) => (
        <span className={`badge badge-${value ? 'success' : 'warning'}`}>
          {value ? <><CheckCircle2 size={11} /> Publicado</> : <><FilePen size={11} /> Rascunho</>}
        </span>
      )
    },
    {
      key: 'created_at',
      label: 'Criado em',
      render: (value) => value ? format(new Date(value), 'dd/MM/yyyy', { locale: ptBR }) : '-'
    }
  ];

  return (
    <AdminLayout>
      <div className="meditation-page">
        <div className="page-header">
          <div>
            <h1><Headphones size={26} /> Meditação</h1>
            <p>Gerencie sessões de meditação e mindfulness</p>
          </div>
          <button className="btn btn-primary" onClick={() => handleOpenModal()}>
            <Plus size={16} /> Adicionar Sessão
          </button>
        </div>

        <div className="analytics-cards-grid">
          <AdminCard
            title="Total de Sessões"
            value={analytics.total_sessions || 0}
            subtitle={`${analytics.published_sessions || 0} publicadas`}
            icon={<Headphones size={22} />}
            color="blue"
          />
          <AdminCard
            title="Usuários Ativos"
            value={analytics.total_users_active || 0}
            subtitle="praticando meditação"
            icon={<Users size={22} />}
            color="purple"
          />
          <AdminCard
            title="Total de Reproduções"
            value={analytics.total_listening_sessions || 0}
            subtitle={`${analytics.total_minutes_listened || 0} minutos ouvidos`}
            icon={<Play size={22} />}
            color="green"
          />
          <AdminCard
            title="Taxa de Conclusão"
            value={`${analytics.completion_rate || 0}%`}
            subtitle={`${analytics.completed_sessions || 0} sessões completas`}
            icon={<CheckCircle size={22} />}
            color="yellow"
          />
        </div>

        <div className="tabs-container">
          <button
            className={`tab-btn ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            <List size={15} /> Todas ({sessions.length})
          </button>
          <button
            className={`tab-btn ${activeTab === 'emergency' ? 'active' : ''}`}
            onClick={() => setActiveTab('emergency')}
          >
            <Zap size={15} /> Emergência ({sessions.filter(s => s.is_emergency).length})
          </button>
          <button
            className={`tab-btn ${activeTab === 'premium' ? 'active' : ''}`}
            onClick={() => setActiveTab('premium')}
          >
            <Star size={15} /> Premium ({sessions.filter(s => s.is_premium).length})
          </button>
          <button
            className={`tab-btn ${activeTab === 'published' ? 'active' : ''}`}
            onClick={() => setActiveTab('published')}
          >
            <CheckCircle2 size={15} /> Publicadas ({sessions.filter(s => s.published).length})
          </button>
          <button
            className={`tab-btn ${activeTab === 'draft' ? 'active' : ''}`}
            onClick={() => setActiveTab('draft')}
          >
            <FilePen size={15} /> Rascunhos ({sessions.filter(s => !s.published).length})
          </button>
        </div>

        <div className="table-section">
          <AdminTable
            columns={columns}
            data={filteredSessions}
            loading={loading}
            actions={(row) => (
              <div className="table-actions">
                <button
                  className="table-action-btn edit"
                  onClick={() => handleOpenModal(row)}
                  title="Editar sessão"
                >
                  <Edit2 size={14} /> Editar
                </button>
                <button
                  className={`table-action-btn ${row.published ? 'warning' : 'success'}`}
                  onClick={() => handleTogglePublish(row.id, row.published)}
                  title={row.published ? 'Despublicar' : 'Publicar'}
                >
                  {row.published ? <><FilePen size={14} /> Despublicar</> : <><CheckCircle size={14} /> Publicar</>}
                </button>
                <button
                  className="table-action-btn delete"
                  onClick={() => handleDelete(row.id)}
                  title="Excluir sessão"
                >
                  <Trash2 size={14} /> Excluir
                </button>
              </div>
            )}
          />
        </div>

        <AdminModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditing(null);
          }}
          title={editing ? <><Edit2 size={20} /> Editar Sessão</> : <><Plus size={20} /> Adicionar Nova Sessão</>}
        >
          <form onSubmit={handleSave} className="content-form">
            <div className="form-group">
              <label htmlFor="title">Título *</label>
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                placeholder="Ex: Respiração 4-7-8, Meditação Guiada para Dormir"
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Descrição *</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                rows="4"
                placeholder="Descreva a sessão de meditação..."
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="duration_seconds">Duração (segundos) *</label>
                <input
                  type="number"
                  id="duration_seconds"
                  value={formData.duration_seconds}
                  onChange={(e) => setFormData({ ...formData, duration_seconds: e.target.value })}
                  required
                  min="30"
                  placeholder="600"
                />
                <small>{formatDuration(parseInt(formData.duration_seconds) || 0)}</small>
              </div>

              <div className="form-group">
                <label htmlFor="category">Categoria *</label>
                <select
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                >
                  <option value="emergency">Emergência</option>
                  <option value="breathing">Respiração</option>
                  <option value="body_scan">Body Scan</option>
                  <option value="mindfulness">Mindfulness</option>
                  <option value="sleep">Sono</option>
                  <option value="stress_relief">Alívio de Estresse</option>
                  <option value="urge_surfing">Urge Surfing</option>
                  <option value="gratitude">Gratidão</option>
                  <option value="visualization">Visualização</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="difficulty_level">Nível de Dificuldade *</label>
                <select
                  id="difficulty_level"
                  value={formData.difficulty_level}
                  onChange={(e) => setFormData({ ...formData, difficulty_level: e.target.value })}
                  required
                >
                  <option value="EASY">Fácil</option>
                  <option value="MEDIUM">Médio</option>
                  <option value="HARD">Difícil</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="instructor">Instrutor</label>
                <input
                  type="text"
                  id="instructor"
                  value={formData.instructor}
                  onChange={(e) => setFormData({ ...formData, instructor: e.target.value })}
                  placeholder="Nome do instrutor"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="audio">Arquivo de Áudio {!editing && '*'}</label>
              <input
                type="file"
                id="audio"
                accept="audio/mp3,audio/wav,audio/m4a,audio/ogg,audio/aac"
                onChange={(e) => setAudioFile(e.target.files[0])}
                required={!editing}
              />
              <small>Formatos aceitos: MP3, WAV, M4A, OGG, AAC (máx. 50MB)</small>
              {editing && formData.audio_url && (
                <small style={{ display: 'block', marginTop: '0.5rem', color: '#7E72E3' }}>
                  Arquivo atual: {formData.audio_url}
                </small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="thumbnail">Imagem Thumbnail</label>
              <input
                type="file"
                id="thumbnail"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                onChange={(e) => setThumbnailFile(e.target.files[0])}
              />
              <small>Formatos aceitos: JPEG, PNG, GIF, WebP (máx. 5MB)</small>
              {editing && formData.thumbnail_url && (
                <small style={{ display: 'block', marginTop: '0.5rem', color: '#7E72E3' }}>
                  Imagem atual: {formData.thumbnail_url}
                </small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="tags">Tags (separadas por vírgula)</label>
              <input
                type="text"
                id="tags"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="ansiedade, respiração, rápido"
              />
            </div>

            <div className="form-group">
              <label htmlFor="benefits">Benefícios (separados por vírgula)</label>
              <input
                type="text"
                id="benefits"
                value={formData.benefits}
                onChange={(e) => setFormData({ ...formData, benefits: e.target.value })}
                placeholder="Reduz ansiedade, Acalma rapidamente"
              />
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.is_emergency}
                  onChange={(e) => setFormData({ ...formData, is_emergency: e.target.checked })}
                />
                <span><Zap size={14} /> Sessão de Emergência (1-3 min)</span>
              </label>
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.is_premium}
                  onChange={(e) => setFormData({ ...formData, is_premium: e.target.checked })}
                />
                <span><Star size={14} /> Conteúdo Premium</span>
              </label>
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.published}
                  onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                />
                <span><CheckCircle size={14} /> Publicar imediatamente (visível no app)</span>
              </label>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => setShowModal(false)}
              >
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary">
                {editing ? <><Save size={16} /> Salvar Alterações</> : <><Plus size={16} /> Adicionar Sessão</>}
              </button>
            </div>
          </form>
        </AdminModal>
      </div>
    </AdminLayout>
  );
};

export default MeditationManagement;
